import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UserSummary } from "@pagemates/shared";
import { askForPush } from "./PushAsk";
import { api, describeError } from "../lib/api";
import { AskIcon, CloseIcon, LockIcon, PlusIcon, QuoteIcon, ReplyIcon, SendIcon } from "./Icons";
import { posLabel, type PosCtx } from "../lib/format";
import { talk } from "@pagemates/shared/copy";

const txt = talk.composer;

type Mode = "open" | "here" | "custom" | "landmark";
type Kind = "message" | "quote" | "question";

export interface ComposerHandle { replyTo(name: string): void; }

/** The thread this composer sits under, when it is a question. */
export interface QuestionContext { asker: string; position: number; mine: boolean }

const listNames = (names: string[]) => names.length <= 2 ? names.join(txt.andSep) : txt.and(names.slice(0, -1).join(", "), names[names.length - 1]);

/**
 * The chat pill: a plus for the special kinds, the growing textarea, the
 * spoiler chip and send. Anything that needs explaining goes in a strip above
 * the pill, which only appears when there is something to say: a quote, a
 * question for people ahead, an answer to one, or a reply to someone.
 */
export interface ComposerExtra { key: string; label: string; about: string; icon: React.ReactNode; onPick: () => void }

export function Composer({ clubId, bookId, herePercent, hereLabel, parentId, placeholder, register, onPosted, ahead, question, myProgress, myDone, ctx, extras }:
  { clubId: string; bookId: string; herePercent: number; hereLabel: string; parentId?: string | null; placeholder: string;
    /** more + menu items that open their own sheet (leave a note forward, make a prediction) */
    extras?: ComposerExtra[];
    register?: (h: ComposerHandle) => void; onPosted?: () => void;
    /** stream only: who could answer a question asked here */
    ahead?: UserSummary[];
    /** thread only: set when the thread is a question */
    question?: QuestionContext; myProgress?: number; myDone?: boolean;
    /** the book's size, this reader's unit and the plan's landmarks, so positions read as "46% · p. 180" or "Part One · 52%" */
    ctx: PosCtx }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [mode, setMode] = useState<Mode>("here");
  const [custom, setCustom] = useState(herePercent);
  const [landmark, setLandmark] = useState<number | null>(null); // percent of a chosen landmark
  const [open, setOpen] = useState(false);
  const [plus, setPlus] = useState(false);
  const [kind, setKind] = useState<Kind>("message");
  const [replyName, setReplyName] = useState<string | null>(null);
  const [spoiler, setSpoiler] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const plusRef = useRef<HTMLDivElement>(null);

  const answering = Boolean(parentId && question && !question.mine);
  const spoilerAt = myDone ? 100 : Math.max(question?.position ?? 0, myProgress ?? 0);
  const locked = kind === "question" || Boolean(parentId && question);
  const position = kind === "question" ? herePercent
    : parentId && question ? (spoiler ? spoilerAt : question.position)
    : mode === "open" ? 0 : mode === "here" ? herePercent : mode === "landmark" ? (landmark ?? herePercent) : custom;

  const post = useMutation({
    mutationFn: () => api.addComment(clubId, bookId, {
      body: replyName ? `@${replyName} ${body}` : body, position, parentId: parentId ?? null,
      kind: parentId ? "message" : kind, ...(answering ? { spoiler } : {}),
    }),
    onSuccess: async () => {
      if (!parentId && kind === "question") askForPush("question"); // "Want to know when someone answers?"
      setBody(""); setKind("message"); setReplyName(null); setSpoiler(false);
      if (ta.current) ta.current.style.height = "auto";
      await qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] });
      onPosted?.();
    },
  });

  useEffect(() => {
    register?.({ replyTo(name) { setReplyName(name); ta.current?.focus(); } });
  }, [register]);
  useEffect(() => {
    if (!plus) return;
    const close = (e: MouseEvent) => { if (!plusRef.current?.contains(e.target as Node)) setPlus(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [plus]);

  function pick(k: Kind) { setKind(k); setPlus(false); ta.current?.focus(); }
  function grow(el: HTMLTextAreaElement) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 7.2 * 16)}px`; }
  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) {
      e.preventDefault();
      if (body.trim() && !post.isPending) post.mutate();
    }
  }
  const chipText = position === 0 && !locked ? txt.open : mode === "landmark" && !locked ? posLabel(position, ctx) : `${position}%`;
  const aheadNames = (ahead ?? []).map((u) => u.name);

  return (
    <form className="composer" onSubmit={(e) => { e.preventDefault(); if (body.trim()) post.mutate(); }}>
      {open && !locked && (
        <div className="popover" role="radiogroup" aria-label={txt.whoCanSeeThis}>
          <button type="button" role="radio" aria-checked={mode === "open"} onClick={() => { setMode("open"); setOpen(false); }}>
            <b>{txt.spoilerFree}</b><span>{txt.everyoneSeesItWhatever}</span>
          </button>
          <button type="button" role="radio" aria-checked={mode === "here"} onClick={() => { setMode("here"); setOpen(false); }}>
            <b>{hereLabel} · {herePercent}%</b><span>{txt.sealedForAnyoneWho}</span>
          </button>
          {(ctx.landmarks?.length ?? 0) > 0 && <div className="grp">{txt.landmarks}</div>}
          {ctx.landmarks?.map((l) => (
            <button key={l.name + l.percent} type="button" role="radio" className="lm" aria-checked={mode === "landmark" && landmark === l.percent} onClick={() => { setMode("landmark"); setLandmark(l.percent); setOpen(false); }}>
              <b>{txt.through(l.name)}<small>{l.percent}%{l.page && (ctx.myFormat ?? "pages") === "pages" ? txt.p(l.page) : ""}</small></b>
            </button>
          ))}
          <button type="button" role="radio" aria-checked={mode === "custom"} className="custom"
            onClick={(e) => { setMode("custom"); (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.focus(); }}>
            <span><b>{txt.somewhereElse}</b><span>{txt.pickThePercentIt}</span></span>
            <input className="input num" type="number" min={0} max={100} value={custom} aria-label={txt.percent}
              onClick={(e) => e.stopPropagation()} onFocus={() => setMode("custom")}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setOpen(false); } }}
              onChange={(e) => setCustom(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} />
          </button>
        </div>
      )}

      {kind === "quote" && (
        <div className="mode-strip">
          <QuoteIcon /><div className="t"><b>{txt.quoteFromTheBook}</b><small>{txt.inTheBookS}</small></div>
          <button type="button" className="x" aria-label={txt.backToAPlain} onClick={() => setKind("message")}><CloseIcon /></button>
        </div>
      )}
      {kind === "question" && (
        <div className="mode-strip">
          <AskIcon /><div className="t"><b>{txt.questionForPeopleAhead}</b>
            <small>{txt.sayWhereYouAre(aheadNames.length ? txt.pastAndWillAnswer(listNames(aheadNames), aheadNames.length === 1 ? txt.is : txt.are, posLabel(herePercent, ctx)) : txt.nobodyIsPastYet(posLabel(herePercent, ctx)))}</small></div>
          <button type="button" className="x" aria-label={txt.backToAPlain} onClick={() => setKind("message")}><CloseIcon /></button>
        </div>
      )}
      {answering && question && (
        <div className={`mode-strip${spoiler ? " spoiler" : ""}`}>
          <LockIcon />
          {spoiler ? (
            <div className="t"><b>{txt.spoilerAnswer}</b><small>{txt.sealedForUntil(question.asker, posLabel(spoilerAt, ctx))}<button type="button" className="link" onClick={() => setSpoiler(false)}>{txt.keepItSpoilerFree}</button></small></div>
          ) : (
            <div className="t"><b>{txt.answeringWhoSAt(question.asker, posLabel(question.position, ctx))}</b><small>{txt.notSureWhatThey}<button type="button" className="link" onClick={() => setSpoiler(true)}>{txt.markItAsA}</button>{txt.andItOpensWhen}</small></div>
          )}
        </div>
      )}
      {replyName && (
        <div className="mode-strip reply">
          <ReplyIcon /><div className="t">{txt.replyingTo}<b>{replyName}</b></div>
          <button type="button" className="x" aria-label={txt.stopReplying} onClick={() => setReplyName(null)}><CloseIcon /></button>
        </div>
      )}

      <div className="pill-row">
        {!parentId && (
          <div className="plus-wrap" ref={plusRef}>
            <button type="button" className={`plus${plus ? " on" : ""}`} aria-label={txt.moreWaysToPost} aria-expanded={plus} onClick={() => setPlus((p) => !p)}><PlusIcon /></button>
            {plus && (
              <div className="popover plus-menu" role="menu">
                <button type="button" role="menuitem" className="opt" onClick={() => pick("quote")}>
                  <span className="bub"><QuoteIcon /></span><span><b>{txt.shareAQuote}</b><span>{txt.aPassageFromThe}</span></span>
                </button>
                <button type="button" role="menuitem" className="opt" onClick={() => pick("question")}>
                  <span className="bub"><AskIcon /></span><span><b>{txt.askPeopleAhead}</b><span>{txt.aQuestionForReaders}</span></span>
                </button>
                {extras?.map((x) => (
                  <button key={x.key} type="button" role="menuitem" className="opt" onClick={() => { setPlus(false); x.onPick(); }}>
                    <span className="bub">{x.icon}</span><span><b>{x.label}</b><span>{x.about}</span></span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className={`pill-box${kind === "quote" ? " quoting" : ""}`}>
          <textarea ref={ta} id={`compose-${parentId ?? "top"}`} rows={1} value={body}
            placeholder={kind === "quote" ? txt.pasteThePassage : kind === "question" ? txt.yourQuestion : answering ? txt.answerNoSpoilers : placeholder}
            maxLength={kind === "quote" ? 1000 : 4000}
            onChange={(e) => { setBody(e.target.value); grow(e.target); }} onKeyDown={onKey} />
          <div className="pill-tail">
            {locked ? (
              <span className="chip locked" aria-label={txt.postedAt(position)}><LockIcon width={11} height={11} />{position}%</span>
            ) : (
              <button type="button" className={`chip${mode === "open" ? " open" : ""}`} aria-label={txt.visibility(chipText)} aria-expanded={open} onClick={() => setOpen((o) => !o)}>{chipText}</button>
            )}
            <button type="submit" className="send" disabled={!body.trim() || post.isPending} aria-label={parentId ? txt.reply : txt.send}><SendIcon /></button>
          </div>
        </div>
      </div>
      {post.isError && <p className="error hint-line">{describeError(post.error)}</p>}
    </form>
  );
}
